import constants from '@Common/Core/Constant';
import { enumerable } from '@Common/Core/CoreDecorator';
import { destroyEvents } from '@Common/Core/Destroyable';
import { properties } from '@Common/Core/Properties';

import { inputType } from '@Common/Enums';
import formatHelper from '@Common/Helpers/FormatHelper';
import localizationHelper from '@Common/Helpers/LocalizationHelper';
import timeHelper from '@Common/Helpers/TimeHelper';
import FormatDotNetStyle from '@Libs/FormatDotNetStyle';
import { BaseControl, IControlParameters } from './BaseControl';
import { IMargins } from './Controls';
import { Popup } from './Popup';
import { Button } from './SimpleButton';
import { TimeSpan } from './TimeSpan';

export interface IInputParameters extends IControlParameters {
    width?: any;
    height?: any;
    type?: string;
    canEdit?: boolean;
    rowsCount?: number;
    margins?: IMargins;
    showTime?: boolean;
    blockElement?: boolean;
    isDesktop?: boolean;
    floatFormat?: string;
    wrap?: string;
    timeOnly?: boolean;
    inputFormat?: string;
    showFormatInPlaceHolder?: boolean;
    hideSymbols?: boolean;
    readonly?: boolean;
    preventKeyBubbling?: boolean;
    defaultPasswordMask?: string;
    canBeNull?: boolean;
}

export class Input extends BaseControl {
    private _onValidateCallbacks: IBooleanCallback[] = [];
    private _onChange = _.noop;
    private _onEnterPressed = _.noop;
    private _onSelect = _.noop;
    private _onOpenPopup = _.noop;
    private _onClosePopup = _.noop;
    private _onFocusIn = _.noop;
    private _onFocusOut = _.noop;

    private _autoChanging = false;

    private _inputFormat: string;
    private _pickerPropertyName: string;

    private _desktopDatepickerInput: JQuery<HTMLElement>;
    private _dateTimeInput: JQuery<HTMLElement>;
    private _ipadInput: JQuery<HTMLElement>;
    private _dateInputRoot: JQuery<HTMLElement>;

    private _value: any;

    constructor(param?: IInputParameters) {
        super(
            _.defaults(param, {
                css: {},
                className: '',
                width: 'auto',
                height: '30px',

                type: inputType.string,
                canEdit: true,
                rowsCount: 5,
                showTime: false,
                timeOnly: false,
                blockElement: false,
                isDesktop: !properties.iOSVersion,
                floatFormat: '0.00',
                hideSymbols: false,
                inputFormat: null,
                showFormatInPlaceHolder: true,
                preventKeyBubbling: false,

                validator: null,

                margins: {},

                defaultPasswordMask: '********',
                readonly: undefined
            })
        );

        this._param.margins = $.extend(
            {
                left: false,
                right: false,
                top: false,
                bottom: false
            },
            (param && param.margins) || undefined
        );

        this._autoChanging = false;

        // set input format
        if (this._param.inputFormat) {
            this._inputFormat = this._param.inputFormat;
        } else if (this._param.type === inputType.timeSpan) {
            this._inputFormat = constants.dayTimeSpanFormat;
        } else if (this.isDateTime) {
            this._inputFormat = localizationHelper.getInputDateTimeFormat(this._param.showTime, this._param.timeOnly);
            if (!this._param.validator) {
                this._param.validator = () => {
                    const date = this.getValue(true);
                    let valid = (this._param.canBeNull && this._dateTimeInput.val().toString() == '') || !(date == null || isNaN(date.getTime()));
                    if (!valid) {
                        this.setError(strings.wizards.time.invalidValue);
                        return false;
                    }
                    this.removeError();
                    return true;
                };
            }
        }

        this._pickerPropertyName = this._param.showTime ? 'datetimepicker' : 'datepicker';

        switch (this._param.type) {
            case inputType.string:
            case inputType.bool:
            case inputType.guid:
                this._root = $('<input class="text-input" autocomplete="new-password" />').attr(
                    'type',
                    this._param.hideSymbols ? 'password' : 'text'
                );
                break;
            case inputType.dateTime:
                this._root = this.createDateInput();
                break;
            case inputType.float:
                this._root = $('<input type="text" class="text-input" />').onlyNumbers({
                    allowFloat: true,
                    allowPercent: true
                });
                break;
            case inputType.int:
                this._root = $('<input type="text" class="text-input" pattern="[0-9]*"/>').onlyNumbers({
                    allowPercent: true
                });
                break;
            case inputType.text:
                this._root = $('<textarea class="text-input" />').attr({
                    rows: this._param.rowsCount,
                    wrap: this._param.wrap
                });
                break;
            case inputType.url:
                this._root = $('<input type="url" class="text-input" />');
                break;
            case inputType.timeSpan:
                this._root = this.createTimeSpanInput();
                break;
            default:
                throw new Error('Unknown input type');
        }

        if (this._root) {
            this._param.className && this._root.addClass(this._param.className);

            if (this._param.blockElement) {
                this._root.css('display', 'block');
            }

            this.setState(null, !this._param.canEdit);

            if (this._param.isDesktop && this.isDateTime) {
                this._root.css('width', this._param.width);
                this._root.find('input').css('height', this._param.height);
            } else {
                this._root.css('width', this._param.width).css('height', this._param.height);
            }

            this._root.applyMargins(this._param.margins);

            this._root
                .css(this._param.css)
                .on('change keyup paste cut', e => {
                    this.validate();
                    this.changed(e);
                })
                .blur(() => {
                    this.validate();
                })
                .on('select', e => {
                    this._onSelect(e);
                })
                .focusin(() => {
                    if (this._param.hideSymbols) {
                        this._autoChanging = true;
                        this._root.val('');
                        this._autoChanging = false;
                    }
                    if (this._onFocusIn) {
                        this._onFocusIn();
                    }
                })
                .focusout(() => {
                    if (this._param.hideSymbols) {
                        this.setDefaultPasswordMask();
                    }
                    if (this._onFocusOut) {
                        this._onFocusOut();
                    }
                });

            if (this._param.preventKeyBubbling) {
                this._root.keydown(e => {
                    e.stopPropagation();
                });
            }
        }

        if (this._param.readonly !== undefined) {
            this.readonly(this._param.readonly);
        }
    }

    public setDefaultPasswordMask(): void {
        if (this._value !== '') {
            this._autoChanging = true;
            this._root.val(this._param.defaultPasswordMask);
            this._autoChanging = false;
        }
    }

    private createTimeSpanInput(): JQuery<HTMLElement> {
        const root = $('<input type="text" class="time-span-input" />');

        root.click(() => {
            if (!this._readonly) {
                const popupContent = $('<div class="time-span-popup popup-shadow"/>');

                const timeSpan = new TimeSpan({
                    format: this._inputFormat
                });
                timeSpan.getControl().appendTo(popupContent);
                timeSpan.value(this.getValue());

                timeSpan.onChange(() => {
                    const dateTimeDuration = timeSpan.value();
                    this.setValue(dateTimeDuration, true);
                });

                const button = new Button({
                    text: 'OK',
                    width: 60
                });
                button.getControl().appendTo(popupContent);
                button.onClick(() => {
                    this.setValue(timeSpan.value());
                    popup.hide();
                    this.validate();
                    this.changed();

                    root.trigger('timespanClosed');
                });

                const popup = new Popup({
                    content: popupContent,
                    jointHeight: parseInt(this._param.height), // this._root.outerHeight(true)
                    padding: 0
                });
                popup.show(this._root.offset().left, this._root.offset().top);
            }
        });

        return root;
    }

    private createDateInput(): JQuery<HTMLElement> {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;

        const height = parseInt(this._param.height);

        this._dateInputRoot = $('<div class="date-picker-wrapper"></div>')
            .width(this._param.width)
            .toggleClass('block', this._param.blockElement)
            .toggleClass('ipad', !this._param.isDesktop)
            .toggleClass('desktop', this._param.isDesktop);

        this._dateTimeInput = $('<input class="preview-date" type="text"/>')
            .height(height)
            .change(() => {
                const date = _.isString(this._value) ? timeHelper.parseDateTime(this._value, this._inputFormat) : this._value;
                if (date) {
                    this._desktopDatepickerInput[this._pickerPropertyName]('setDate', date);
                }
            });

        if (this._param.showFormatInPlaceHolder) {
            this._dateTimeInput.attr('placeholder', this._inputFormat);
        }

        if (this._param.isDesktop) {
            this._desktopDatepickerInput = $('<input class="desktop-date" type="text" readonly="readonly"/>').height(height);

            if (this._param.showFormatInPlaceHolder) {
                this._desktopDatepickerInput.attr('placeholder', this._inputFormat);
            }

            $('<div style="overflow:hidden"/>')
                .appendTo(this._dateInputRoot)
                .append(this._desktopDatepickerInput)
                .append(this._dateTimeInput)
                .height(height);

            if (this._desktopDatepickerInput[this._param.timeOnly ? 'timepicker' : this._pickerPropertyName]) {
                this._desktopDatepickerInput[this._param.timeOnly ? 'timepicker' : this._pickerPropertyName]('destroy');
                this._desktopDatepickerInput.removeClass('hasDatepicker').removeAttr('id');
                this._desktopDatepickerInput.unbind();
            }

            const datepickedConfig = {
                changeMonth: true,
                changeYear: true,
                yearRange: 'c-5:c+5',
                showOn: 'button',
                buttonImage: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
                buttonImageOnly: true,
                showButtonPanel: !this._param.timeOnly,
                currentText: this._param.showTime ? strings.timePicker.currentText : strings.timePicker.currentDayText,

                onSelect: () => {
                    if (this._desktopDatepickerInput !== null) {
                        this.setValue(this._desktopDatepickerInput[this._pickerPropertyName]('getDate'), true);
                        this.changed();
                    }
                },
                onClose: () => {
                    this._dateInputRoot.removeClass('dropdownShown');
                    if (this._param.onClosePopup) {
                        this._param.onClosePopup();
                    }
                    if (this._onClosePopup) {
                        this._onClosePopup();
                    }
                },
                onChangeMonthYear: (year, month, inst) => {
                    if (inst.currentYear !== year || inst.currentMonth !== month - 1) {
                        let curDate = inst.input.datepicker('getDate');
                        if (curDate == null) {
                            curDate = new Date(inst.selectedYear, inst.selectedMonth, inst.selectedDay);
                        } else {
                            curDate.setAbsoluteYear(year, month - 1);
                        }
                        inst.input.datepicker('setDate', curDate);
                        inst.settings.onSelect();
                    }
                }
            };
            this._desktopDatepickerInput[this._param.timeOnly ? 'timepicker' : this._pickerPropertyName](datepickedConfig);
            this._dateInputRoot
                .find('img')
                .prependTo(this._dateInputRoot)
                .removeAttr('alt')
                .removeAttr('title')
                .width(height)
                .height(height)
                .click(() => {
                    this._dateInputRoot.addClass('dropdownShown');
                    this._onOpenPopup();
                })
                .css({
                    padding: '2px'
                });
        } else {
            this._dateTimeInput.appendTo(this._dateInputRoot);

            this._ipadInput = this._param.timeOnly
                ? $('<input class="ipad-date" type="time"/>')
                : this._param.showTime
                ? $('<input class="ipad-date" type="datetime-local"/>')
                : $('<input class="ipad-date" type="date"/>');
            if (this._param.showFormatInPlaceHolder) {
                this._ipadInput.attr('placeholder', this._inputFormat);
            }

            this._ipadInput.change(() => {
                const typedValue = self._ipadInput.val()
                    ? formatHelper.dateTimeFromIOSString(self._ipadInput.val().toString(), self._param.showTime, self._param.timeOnly)
                    : null;
                this.setValue(typedValue, true);
                this.changed();
            });

            this._ipadInput.height(height).click(function (): void {
                this.focus();
            });
            this._ipadInput.insertAfter(this._dateTimeInput);
        }

        return this._dateInputRoot;
    }

    public setValue(data: any, isInternalCall = false): void {
        if (data != null && (data !== '' || !this.isDateTime) && (!this.isDateTime || _.isDate(data) || _.isString(data))) {
            if (this.isDateTime) {
                if (_.isString(data)) {
                    data = timeHelper.parseDateTime(data, null);
                    if (data == null) {
                        return;
                    }
                }
                if (!this._param.isDesktop) {
                    this._ipadInput.val(data.format(this._param.timeOnly ? constants.iOSTimeFormat : constants.iOSDateFormat));
                    const formattedValue = data ? data.format(this._inputFormat) : '';
                    this._dateTimeInput.val(formattedValue);

                    // this._ipadInput.val(data.format(this._param.timeOnly ? constants.iOSTimeFormat : constants.iOSDateFormat));
                    // this._ipadInput.change();
                } else {
                    this._dateTimeInput.val(data.format(this._inputFormat));
                    this._desktopDatepickerInput[this._pickerPropertyName]('setDate', data);
                }
            } else if (this._param.type === inputType.float) {
                if (data !== '') {
                    if (typeof data === 'string') {
                        const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());
                        data = formatter.parseNumber(data);
                    }
                    data = formatHelper.formatNumber(data, this._param.floatFormat);
                }
                data === '' ? this._root.val('') : this._root.val(data);
            } else if (this._param.type === inputType.timeSpan) {
                this._root.val(formatHelper.formatMilliseconds(data, this._inputFormat));
            } else {
                this._root.val(data);
            }

            this.validate();
        } else {
            this._root.val('');
            this._dateTimeInput && this._dateTimeInput.val('');
        }

        if (!isInternalCall) {
            this._value = data;
        }

        if (this._param.hideSymbols) {
            this.setDefaultPasswordMask();
        }
    }

    public getValue(mustBeValid = false): any {
        let result = null;
        if (this._param.hideSymbols) {
            return this._value;
        }
        const v = this._root.val().toString();
        if (
            this._param.type === inputType.text ||
            this._param.type === inputType.string ||
            this._param.type === inputType.url ||
            this._param.type === inputType.guid
        ) {
            result = this._root.val();
        } else if (this.isDateTime) {
            result = this._param.isDesktop
                ? timeHelper.parseDateTime(this._dateTimeInput.val().toString(), this._inputFormat, mustBeValid)
                : formatHelper.dateTimeFromIOSString(this._ipadInput.val().toString(), this._param.showTime, this._param.timeOnly);

            if (this._param.timeOnly && result) {
                result = new Date(1, 0, 1, result.getHours(), result.getMinutes(), result.getSeconds(), result.getMilliseconds());
            }
        } else if (this._param.type === inputType.float) {
            const formatter = new FormatDotNetStyle(localizationHelper.getTwoLettersLanguageName());
            result = v !== '' ? formatter.parseNumber(v) : null;
        } else if (this._param.type === inputType.int) {
            result = v !== '' ? parseInt(v) : null;
        } else if (this._param.type === inputType.timeSpan) {
            result = v !== '' ? formatHelper.parseMilliseconds(v) : null;
        }

        return result;
    }

    public setState(enabled: boolean, readonly = false): void {
        if (this._param.isDesktop) {
            if (this.isDateTime) {
                const input = this._root.find('input');

                if (_.isBoolean(enabled)) {
                    input.prop('disabled', !enabled);
                    input.parent().toggleClass('disabled', !enabled);
                    input.toggleClass('disabled', !enabled);
                    this._root.find('img').toggleClass('disabled', !enabled);
                    input[this._pickerPropertyName](enabled ? 'enable' : 'disable');
                } else if (_.isBoolean(readonly)) {
                    input.prop('readonly', readonly);
                    this._root.find('img').toggleClass('disabled', readonly);
                    input[this._pickerPropertyName](!readonly ? 'enable' : 'disable');
                }
            } else {
                if (enabled != null) {
                    this._root.attr('disabled', enabled ? null : 'disabled');
                    this._root.toggleClass('disabled', !enabled);
                }
                if (readonly) {
                    this._root.attr('readonly', readonly.toString());
                }
            }
        } else {
            if (enabled != null) {
                this._root.attr('disabled', enabled ? null : 'disabled');
                this._root.toggleClass('disabled', !enabled);
            }
        }
    }

    public readonly(data?: boolean): boolean {
        const root = this.isDateTime && !this._param.isDesktop ? this._ipadInput : this.root;

        if (data === true) {
            root.addClass('readonly-input');
            root.attr('readonly', data.toString());

            this.isDateTime &&
                this._param.isDesktop &&
                this._desktopDatepickerInput[this._param.timeOnly ? 'timepicker' : this._pickerPropertyName]('disable');
        } else if (data === false) {
            root.removeClass('readonly-input');
            root.removeAttr('readonly');

            this.isDateTime &&
                this._param.isDesktop &&
                this._desktopDatepickerInput[this._param.timeOnly ? 'timepicker' : this._pickerPropertyName]('enable');
        }

        return super.readonly(data);
    }

    public validate(): boolean {
        const value = this.getValue();
        let state = true;
        if (_.isFunction(this._param.validator)) {
            state = this._param.validator(value);
        }
        _(this._onValidateCallbacks).forEach(function (callback: IBooleanCallback): void {
            callback(state);
        });
        return state;
    }

    public adjust(width?: number, height?: number): void {
        if (!height) {
            height = this._param.height;
        }

        this._root.outerWidth(width).outerHeight(height);
    }

    public getCaretPosition(): number {
        const oField = this._root[0];
        // Initialize
        let iCaretPos = 0;

        // IE Support
        const selection = document['selection'];
        if (selection) {
            // Set focus on the element
            oField.focus();

            // To get cursor position, get empty selection range
            const oSel = selection.createRange();

            // Move selection start to 0 position
            oSel.moveStart('character', -oField.textContent.length);

            // The caret position is selection length
            iCaretPos = oSel.text.length;
        } else if (oField['selectionStart'] || oField['selectionStart'] === '0') {
            iCaretPos = oField['selectionStart'];
        }

        // Return results
        return iCaretPos;
    }

    @enumerable
    private get isDateTime(): boolean {
        return this._param.type === inputType.dateTime;
    }

    @enumerable
    private get root(): JQuery<HTMLElement> {
        return this.isDateTime ? this._dateTimeInput : this._root;
    }

    private changed(e?: JQuery.Event): void {
        if (e && e.keyCode === constants.keyCodes.ENTER) {
            this._onEnterPressed(e);
        }
        if (this._autoChanging) {
            return;
        }

        const newValue = this._param.hideSymbols ? this._root.val() : this.getValue();

        if (
            !this._value ||
            !newValue ||
            this._param.type === inputType.timeSpan ||
            (this.isDateTime && this._value.valueOf() !== newValue.valueOf()) ||
            (!this.isDateTime && newValue !== this._value)
        ) {
            this._value = newValue;
            this._onChange(e);
        }
    }

    private error(message?: string): void {
        const isInvalid = !_.isUndefined(message);
        const root = this.root;

        root.toggleClass('invalid', isInvalid);
        root.attr('title', message || '');
    }

    public setError(errorMessage: string): void {
        this.error(errorMessage);
    }

    public removeError(): void {
        this.error();
    }

    public getError(): string | null {
        const root = this.root;
        return root.hasClass('invalid') ? root.attr('title') : null;
    }

    public destroy(): void {
        if (this._desktopDatepickerInput) {
            const pickerProperty = this._param.timeOnly ? 'timepicker' : this._pickerPropertyName;

            if (this._desktopDatepickerInput.data('datepicker')) {
                this._desktopDatepickerInput[pickerProperty]('hide');
                this._desktopDatepickerInput[pickerProperty]('destroy');
                this._desktopDatepickerInput[pickerProperty] = null;
            }
            this._desktopDatepickerInput.remove();
            this._desktopDatepickerInput = null;
            this._dateTimeInput = null;
        }
        this._onValidateCallbacks.length = 0;

        super.destroy();
        destroyEvents(this);
    }

    public disable(): void {
        this.setState(false);
    }

    public enable(): void {
        this.setState(true);
    }

    public enabled(data?: boolean): boolean {
        if (_.isUndefined(data)) {
            return this._enabled;
        }
        this.setState(data);
    }

    public focus(): Input {
        this._root.focus();
        return this;
    }

    public select(): Input {
        this._root.select();
        return this;
    }

    public onChange(handler: IEventCallback): void {
        if (_.isFunction(handler)) {
            this._onChange = handler;
        }
    }

    public onEnterPressed(handler: IEventCallback): void {
        if (_.isFunction(handler)) {
            this._onEnterPressed = handler;
        }
    }

    public onSelect(handler: IEventCallback): void {
        if (_.isFunction(handler)) {
            this._onSelect = handler;
        }
    }

    public onValidate(handler: (isValid: boolean) => void): void {
        if (_.isFunction(handler)) {
            this._onValidateCallbacks.push(handler);
        }
    }

    public onOpenPopup(handler: IHandler): void {
        if (_.isFunction(handler)) {
            this._onOpenPopup = handler;
        }
    }

    public onClosePopup(handler: IHandler): void {
        if (_.isFunction(handler)) {
            this._onClosePopup = handler;
        }
    }

    public onFocusIn(handler: IEventCallback): void {
        if (_.isFunction(handler)) {
            this._onFocusIn = handler;
        }
    }

    public onFocusOut(handler: IEventCallback): void {
        if (_.isFunction(handler)) {
            this._onFocusOut = handler;
        }
    }
}
